import { useRef, useCallback, useMemo, useState, useEffect } from 'react';
import { TEMPLATE_REGISTRY } from '../../../utils/editableTemplatesRegistry';

interface TemplateEditorModalProps {
  templateId: string;
  templateTitle: string;
  templateCredits: number;
  isProcessing: boolean;
  onDownload: (cleanHtml: string) => Promise<void>;
  onClose: () => void;
}

const TemplateEditorModal = ({
  templateId,
  templateTitle,
  templateCredits,
  isProcessing,
  onDownload,
  onClose,
}: TemplateEditorModalProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState(1100);
  const template = TEMPLATE_REGISTRY[templateId];

  const editableHtml = useMemo(
    () => template?.buildEditable() ?? '<p style="padding:40px;font-family:Arial">Template not found.</p>',
    [templateId] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Auto-resize iframe to content height after load
  const handleIframeLoad = useCallback(() => {
    try {
      const doc = iframeRef.current?.contentDocument;
      if (doc?.body) {
        const h = doc.body.scrollHeight;
        if (h > 200) setIframeHeight(h + 40);
      }
    } catch {
      // cross-origin guard — shouldn't happen with srcDoc
    }
  }, []);

  useEffect(() => {
    // Also try after a short delay for content that renders async (calc scripts)
    const t = setTimeout(handleIframeLoad, 300);
    return () => clearTimeout(t);
  }, [handleIframeLoad]);

  const handleDownload = useCallback(async () => {
    const iframeDoc = iframeRef.current?.contentDocument;
    if (!iframeDoc || !template) return;
    const data = template.extractData(iframeDoc);
    const cleanHtml = template.buildClean(data);
    await onDownload(cleanHtml);
  }, [onDownload, template]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      {/* ── Top bar ── */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-white flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer px-3 py-1.5 rounded-lg hover:bg-gray-100 whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line text-sm"></i>
            </div>
            Back
          </button>
          <div className="h-5 w-px bg-gray-200"></div>
          <div>
            <span className="font-semibold text-gray-900 text-sm">{templateTitle}</span>
            <span className="text-xs text-gray-400 ml-2 hidden sm:inline">
              Click any field to edit directly on the document
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="hidden sm:flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5">
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-coin-line text-amber-600 text-xs"></i>
            </div>
            <span className="text-amber-700 text-xs font-semibold whitespace-nowrap">
              {templateCredits} credits
            </span>
          </div>

          <button
            onClick={handleDownload}
            disabled={isProcessing}
            className="flex items-center gap-2 bg-gray-900 text-white text-sm font-semibold px-5 py-2.5 rounded-xl hover:bg-gray-800 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isProcessing ? (
              <>
                <i className="ri-loader-4-line animate-spin text-sm"></i>
                Saving…
              </>
            ) : (
              <>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-download-line text-green-400 text-sm"></i>
                </div>
                Download Document
              </>
            )}
          </button>
        </div>
      </div>

      {/* ── Info strip ── */}
      <div className="bg-amber-50 border-b border-amber-200 px-5 py-2.5 flex-shrink-0">
        <p className="text-sm text-amber-800 text-center font-medium">
          <span className="inline-block w-3 h-3 rounded-sm bg-amber-200 border border-amber-400 mr-1 align-middle"></span>
          All <strong>highlighted yellow fields</strong> need to be filled in &nbsp;&mdash;&nbsp; click directly on any yellow field to type your info &nbsp;&mdash;&nbsp; then hit <strong>Download Document</strong> when done
        </p>
      </div>

      {/* ── Document canvas ── */}
      <div className="flex-1 overflow-auto bg-gray-200 py-8 px-4">
        <div
          className="mx-auto bg-white"
          style={{
            width: '794px',
            minHeight: `${iframeHeight}px`,
            boxShadow: '0 4px 32px rgba(0,0,0,0.18)',
          }}
        >
          <iframe
            ref={iframeRef}
            srcDoc={editableHtml}
            title={`${templateTitle} Editor`}
            onLoad={handleIframeLoad}
            style={{
              width: '794px',
              height: `${iframeHeight}px`,
              border: 'none',
              display: 'block',
            }}
            sandbox="allow-scripts allow-same-origin"
          />
        </div>
        <div style={{ height: '40px' }} />
      </div>
    </div>
  );
};

export default TemplateEditorModal;
